import type { DateTime } from "luxon";
import { type LoadOrderEntry, orderFilesChronologically } from "../../loadOrder";
import { type DataLoadFile, type DataLoadFileKind, KIND_LABEL, KIND_ORDER, MODE_LABEL, ROSTER_ACTION_LABEL } from "./consts";

/**
 * Only shifts files are dated. Roster and template files split a department by PEOPLE (one export or
 * rotation per unit/wing), so they have no chronology to sort on and keep the order they were listed
 * in — which still decides the outcome, since for a person present in two roster files the first one
 * loaded wins.
 */
const dateOfFile = (file: DataLoadFile): DateTime | null => (file.kind === "shifts" ? file.earliestDate : null);

const filesOfKind = (files: DataLoadFile[], kind: DataLoadFileKind): DataLoadFile[] => files.filter((file) => file.kind === kind);

/**
 * The flat load order across kinds: roster, then shifts oldest-first, then template. Files with no
 * kind yet are excluded — Run is gated on there being none of them.
 */
export function buildOrderedFiles(files: DataLoadFile[]): DataLoadFile[] {
	return KIND_ORDER.flatMap((kind) => orderFilesChronologically(filesOfKind(files, kind), dateOfFile));
}

/** The caption's label carries the choices, so a mis-set kind or action is visible before Run. */
function labelFor(file: DataLoadFile): string {
	if (!file.kind) return file.file.name;
	const action = file.kind === "roster" ? ` (${ROSTER_ACTION_LABEL[file.rosterAction].toLowerCase()})` : "";
	const mode = file.mode === "direct" ? ` [${MODE_LABEL.direct.toLowerCase()}]` : "";
	return `${KIND_LABEL[file.kind]}${action}${mode}: ${file.file.name}`;
}

export function buildLoadOrderEntries(orderedFiles: DataLoadFile[]): LoadOrderEntry[] {
	return orderedFiles.map((file) => ({ id: file.id, label: labelFor(file), date: dateOfFile(file) }));
}

/**
 * A file is loadable once it has a payload to send: the original in direct mode, the transformed CSV
 * otherwise. Skipped and already-loaded files are done, not pending.
 */
export function isPending(file: DataLoadFile): boolean {
	if (file.skip || file.loadResult) return false;
	return file.mode === "direct" || file.transformedData !== null;
}

/**
 * The only file whose own Upload button may be pressed. The order is what makes a batch behave like
 * one file, and a per-card button that ignored it would quietly undo that.
 */
export function nextPendingFile(orderedFiles: DataLoadFile[]): DataLoadFile | undefined {
	return orderedFiles.find(isPending);
}

/**
 * Measured over EVERY file, not over the ones that produced a result: a file whose transform failed
 * has no payload, so checking only the outcomes reports "all done" while that file sits unhandled.
 */
export function allFilesSettled(files: DataLoadFile[]): boolean {
	return files.length > 0 && files.every((file) => file.skip || file.loadResult !== null || file.transformError !== null);
}
